'use client';

import React, { useState } from 'react';
import {
  Zap,
  Mail,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

type AlertStatus = 'sent' | 'pending' | 'acknowledged' | 'failed';

interface SpikeEvent {
  id: string;
  date: string;
  query: string;
  impressions: number;
  baseline: number;
  aboveBaseline: number;
  clicks: number;
  alertStatus: AlertStatus;
  trigger: string;
  acknowledged: boolean;
}

const spikeEvents: SpikeEvent[] = [
  {
    id: 'spike-001',
    date: 'Mar 28, 2026',
    query: 'acme',
    impressions: 6940,
    baseline: 3800,
    aboveBaseline: 83,
    clicks: 2480,
    alertStatus: 'sent',
    trigger: 'Viral Twitter/X thread by @techinfluencer',
    acknowledged: true,
  },
  {
    id: 'spike-002',
    date: 'Mar 28, 2026',
    query: 'acme.io',
    impressions: 3210,
    baseline: 1740,
    aboveBaseline: 84,
    clicks: 1180,
    alertStatus: 'sent',
    trigger: 'Viral Twitter/X thread by @techinfluencer',
    acknowledged: true,
  },
  {
    id: 'spike-003',
    date: 'Feb 26, 2026',
    query: 'acme',
    impressions: 6120,
    baseline: 3800,
    aboveBaseline: 61,
    clicks: 2140,
    alertStatus: 'sent',
    trigger: 'Product Hunt launch — #3 Product of the Day',
    acknowledged: true,
  },
  {
    id: 'spike-004',
    date: 'Feb 26, 2026',
    query: 'acme dashboard',
    impressions: 1890,
    baseline: 1020,
    aboveBaseline: 85,
    clicks: 580,
    alertStatus: 'sent',
    trigger: 'Product Hunt launch — #3 Product of the Day',
    acknowledged: false,
  },
  {
    id: 'spike-005',
    date: 'Jan 27, 2026',
    query: 'acme',
    impressions: 5840,
    baseline: 3800,
    aboveBaseline: 54,
    clicks: 1920,
    alertStatus: 'sent',
    trigger: 'TechCrunch article: "Top 10 Tools for 2026"',
    acknowledged: true,
  },
  {
    id: 'spike-006',
    date: 'Jan 27, 2026',
    query: 'acme review',
    impressions: 1540,
    baseline: 480,
    aboveBaseline: 221,
    clicks: 390,
    alertStatus: 'acknowledged',
    trigger: 'TechCrunch article: "Top 10 Tools for 2026"',
    acknowledged: true,
  },
  {
    id: 'spike-007',
    date: 'Apr 6, 2026',
    query: 'acme',
    impressions: 5240,
    baseline: 3800,
    aboveBaseline: 38,
    clicks: 1820,
    alertStatus: 'pending',
    trigger: 'Detection in progress — email queued',
    acknowledged: false,
  },
  {
    id: 'spike-008',
    date: 'Apr 4, 2026',
    query: 'acme pricing',
    impressions: 2890,
    baseline: 1680,
    aboveBaseline: 72,
    clicks: 840,
    alertStatus: 'failed',
    trigger: 'Email delivery failed — SMTP error',
    acknowledged: false,
  },
];

const alertStatusConfig: Record<
  AlertStatus,
  { label: string; className: string; icon: React.ReactNode }
> = {
  sent: {
    label: 'Alert Sent',
    className: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    icon: <CheckCircle2 size={11} />,
  },
  pending: {
    label: 'Pending',
    className: 'bg-[rgba(150,25,50,0.06)] text-[#961932] border border-[rgba(150,25,50,0.2)]',
    icon: <Clock size={11} />,
  },
  acknowledged: {
    label: 'Acknowledged',
    className: 'bg-[rgb(224,240,253)] text-[#39364E] border border-[rgba(57,54,78,0.2)]',
    icon: <CheckCircle2 size={11} />,
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-50 text-red-700 border border-red-200',
    icon: <AlertCircle size={11} />,
  },
};

type SortField = 'date' | 'impressions' | 'aboveBaseline' | 'clicks';
type SortDir = 'asc' | 'desc';

export default function SpikeEventsTable() {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filterStatus, setFilterStatus] = useState<AlertStatus | 'all'>('all');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleResendAlert = (event: SpikeEvent) => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1200)),
      {
        loading: 'Resending alert…',
        success: `Alert resent for "${event.query}" spike on ${event.date}`,
        error: 'Failed to resend alert',
      }
    );
  };

  const filtered = spikeEvents.filter(
    (e) => filterStatus === 'all' || e.alertStatus === filterStatus
  );

  const sorted = [...filtered].sort((a, b) => {
    let valA: number | string = 0;
    let valB: number | string = 0;
    if (sortField === 'date') { valA = a.date; valB = b.date; }
    if (sortField === 'impressions') { valA = a.impressions; valB = b.impressions; }
    if (sortField === 'aboveBaseline') { valA = a.aboveBaseline; valB = b.aboveBaseline; }
    if (sortField === 'clicks') { valA = a.clicks; valB = b.clicks; }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return <ChevronDown size={12} className="text-[#e7e7e7]" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={12} className="text-[#961932]" />
    ) : (
      <ChevronDown size={12} className="text-[#961932]" />
    );
  };

  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.04)_0px_4px_12px] overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b border-[#e7e7e7]"
        style={{ backgroundColor: 'rgba(150, 25, 50, 0.04)' }}
      >
        <div className="flex items-center gap-2">
          {/* Smarty crimson icon wrapper */}
          <div
            className="w-[34px] h-[34px] rounded-[5px] flex items-center justify-center"
            style={{ backgroundColor: 'rgb(140, 11, 49)' }}
          >
            <Zap size={15} className="text-white" />
          </div>
          <h2 className="text-[15px] font-600 text-[#39364E]">Spike Events</h2>
          <span
            className="px-2 py-0.5 rounded-full text-[11px] font-600 tabular-nums"
            style={{
              backgroundColor: 'rgba(150, 25, 50, 0.08)',
              color: '#961932',
              border: '1px solid rgba(150, 25, 50, 0.2)',
            }}
          >
            {spikeEvents.length} detected
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-[#696969]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as AlertStatus | 'all')}
            className="text-xs border border-[#e7e7e7] rounded-[5px] px-2 py-1.5 bg-white text-[#39364E] focus:outline-none focus:ring-2 focus:ring-[rgba(150,25,50,0.3)] focus:border-[#961932] cursor-pointer transition-all duration-150"
          >
            <option value="all">All statuses</option>
            <option value="sent">Alert Sent</option>
            <option value="pending">Pending</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="border-b border-[#e7e7e7]"
              style={{ backgroundColor: 'rgba(150, 25, 50, 0.02)' }}
            >
              <th className="text-left px-5 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center gap-1 hover:text-[#39364E] transition-colors"
                >
                  Date <SortIcon field="date" />
                </button>
              </th>
              <th className="text-left px-3 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                Query
              </th>
              <th className="text-right px-3 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                <button
                  onClick={() => handleSort('impressions')}
                  className="flex items-center gap-1 ml-auto hover:text-[#39364E] transition-colors"
                >
                  Impressions <SortIcon field="impressions" />
                </button>
              </th>
              <th className="text-right px-3 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                <button
                  onClick={() => handleSort('aboveBaseline')}
                  className="flex items-center gap-1 ml-auto hover:text-[#39364E] transition-colors"
                >
                  vs Baseline <SortIcon field="aboveBaseline" />
                </button>
              </th>
              <th className="text-right px-3 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                <button
                  onClick={() => handleSort('clicks')}
                  className="flex items-center gap-1 ml-auto hover:text-[#39364E] transition-colors"
                >
                  Clicks <SortIcon field="clicks" />
                </button>
              </th>
              <th className="text-left px-3 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                Alert Status
              </th>
              <th className="text-center px-3 py-3 text-[11px] font-500 uppercase tracking-wide text-[#696969]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((event, i) => {
              const statusCfg = alertStatusConfig[event.alertStatus];
              const isExpanded = expandedRow === event.id;
              return (
                <React.Fragment key={event.id}>
                  <tr
                    className={`
                      group border-b border-[rgba(231,231,231,0.7)] cursor-pointer
                      transition-colors duration-100
                      ${
                        i % 2 === 1 ? 'bg-[rgba(150,25,50,0.015)]' : ''
                      }
                      ${
                        event.alertStatus === 'failed' ?'bg-red-50/30 hover:bg-red-50/60'
                          : event.alertStatus === 'pending' ?'bg-[rgba(150,25,50,0.04)] hover:bg-[rgba(150,25,50,0.07)]' :'hover:bg-[rgba(150,25,50,0.04)]'
                      }
                    `}
                    onClick={() => setExpandedRow(isExpanded ? null : event.id)}
                  >
                    <td className="px-5 py-3">
                      <span className="text-[13px] font-500 text-[#39364E] tabular-nums whitespace-nowrap">
                        {event.date}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-[13px] font-500 text-[#39364E]">
                        {event.query}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-[13px] tabular-nums font-500 text-[#39364E]">
                        {event.impressions.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-700 tabular-nums ${
                          event.aboveBaseline >= 80
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : event.aboveBaseline >= 50
                            ? 'bg-[rgba(150,25,50,0.08)] text-[#961932] border border-[rgba(150,25,50,0.2)]'
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        }`}
                      >
                        <Zap size={10} />
                        +{event.aboveBaseline}%
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-[13px] tabular-nums text-[#39364E]">
                        {event.clicks.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-500 ${statusCfg.className}`}
                      >
                        {statusCfg.icon}
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        {event.alertStatus === 'failed' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendAlert(event);
                            }}
                            className="flex items-center gap-1 px-2 py-1 rounded-[5px] text-[11px] font-500 bg-[#961932] text-white hover:bg-[#8C0B31] active:scale-95 transition-all duration-150"
                            title="Resend alert email"
                          >
                            <Mail size={11} />
                            Resend
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open('#', '_blank');
                          }}
                          className="p-1.5 rounded-[5px] text-[#696969] hover:bg-[rgba(150,25,50,0.06)] hover:text-[#961932] transition-all duration-150"
                          title="View in Search Console"
                        >
                          <ExternalLink size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {/* Expanded row */}
                  {isExpanded && (
                    <tr
                      key={`${event.id}-expanded`}
                      className="border-b border-[rgba(231,231,231,0.7)]"
                      style={{ backgroundColor: 'rgba(150, 25, 50, 0.03)' }}
                    >
                      <td colSpan={7} className="px-5 py-3">
                        <div className="flex items-start gap-3">
                          <div
                            className="w-6 h-6 rounded-[5px] flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: 'rgb(140, 11, 49)' }}
                          >
                            <Zap size={12} className="text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-600 text-[#39364E] mb-0.5">Likely trigger</p>
                            <p className="text-xs text-[#696969]">{event.trigger}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <div>
                                <span className="text-[10px] text-[#696969] uppercase tracking-wide">
                                  Baseline avg
                                </span>
                                <span className="ml-2 text-xs font-600 tabular-nums text-[#39364E]">
                                  {event.baseline.toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] text-[#696969] uppercase tracking-wide">
                                  Spike impressions
                                </span>
                                <span className="ml-2 text-xs font-600 tabular-nums text-[#39364E]">
                                  {event.impressions.toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] text-[#696969] uppercase tracking-wide">
                                  Magnitude
                                </span>
                                <span className="ml-2 text-xs font-700 tabular-nums text-[#961932]">
                                  +{event.aboveBaseline}% above avg
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Zap size={32} className="text-[#e7e7e7] mb-3" />
          <p className="text-sm font-500 text-[#39364E]">No spike events match this filter</p>
          <p className="text-xs text-[#696969] mt-1">Try changing the status filter above</p>
        </div>
      )}

      <div
        className="px-5 py-3 border-t border-[#e7e7e7] flex items-center justify-between"
        style={{ backgroundColor: 'rgba(150, 25, 50, 0.02)' }}
      >
        <p className="text-[11px] text-[#696969]">
          Showing{' '}
          <span className="font-500 text-[#39364E]">{sorted.length}</span> of{' '}
          {spikeEvents.length} spike events
        </p>
        <p className="text-[11px] text-[#696969]">Click any row to see spike details</p>
      </div>
    </div>
  );
}